<template>
  <div
    class="flex w-full flex-col items-center justify-start gap-y-8 rounded-lg bg-white p-8 shadow-lg sm:p-11"
  >
    <span class="w-full text-3xl font-medium">{{ __('Checkout') }}</span>

    <div
      class="scrollbar-thin flex max-h-[440px] w-full flex-col items-center justify-start gap-y-4 overflow-y-auto pr-4 pt-1"
      style="scrollbar-gutter: stable"
    >
      <template v-if="data.product_variants.length > 0">
        <div
          v-for="product in data.product_variants"
          class="relative flex w-full flex-col items-center justify-between rounded-lg border p-4 transition-colors duration-200 hover:bg-gray-50 sm:flex-row"
        >
          <Icon
            @click="handleDeleteProduct(product.variant.id)"
            name="delete"
            class="absolute right-4 top-4 cursor-pointer text-primary"
            type="outlined"
            size="text-2xl"
          ></Icon>

          <div
            class="flex h-full w-full flex-row items-start justify-start gap-x-4"
          >
            <div class="flex h-full min-h-40 items-start justify-start">
              <div
                @click.prevent="
                  redirectToPage(
                    'redesign.candidate.shop.products.show',
                    product.id
                  )
                "
                class="h-32 w-32 cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-200 hover:shadow-lg sm:h-40 sm:w-40"
              >
                <img
                  :src="`/uploads/${product.variant.product.images[0].path}`"
                  :key="product.variant.product.images[0].id"
                  :alt="__('Media image')"
                  class="h-full w-full object-cover"
                />
              </div>
            </div>

            <div
              class="flex h-full flex-col items-start justify-start space-y-2 sm:space-y-4"
            >
              <span class="text-sm font-medium text-zinc-600 sm:text-lg">{{
                product.prodname
              }}</span>
              <div class="flex flex-row items-center justify-center gap-x-2">
                <template
                  v-for="(attribute, index) in product.variant
                    .formatted_attributes"
                >
                  <div
                    v-if="attribute.name === 'Color'"
                    class="flex items-center justify-center gap-x-2 pr-2 text-sm font-medium text-zinc-600 sm:text-lg"
                    :class="
                      index === product.variant.formatted_attributes.length - 1
                        ? 'border-r-0'
                        : 'border-r border-zinc-200'
                    "
                  >
                    <span>{{ attribute.name }}: </span>
                    <div
                      class="h-6 w-6 rounded-full pl-2"
                      :style="{ backgroundColor: attribute.value.value }"
                    ></div>
                  </div>

                  <div
                    v-else="!attribute.name === 'Color'"
                    class="pr-2 text-sm font-medium text-zinc-600 sm:text-lg"
                    :class="
                      index === product.variant.formatted_attributes.length - 1
                        ? 'border-r-0'
                        : 'border-r border-zinc-200'
                    "
                  >
                    {{ attribute.name }}: {{ attribute.value.value }}
                  </div>
                </template>
              </div>
              <div class="flex flex-row items-center justify-start gap-x-2">
                <span class="text-lg font-medium sm:text-2xl">{{
                  product.variant.product.points
                }}</span>
                <Icon name="jobful_points_variant" class="text-primary"></Icon>
              </div>
            </div>
          </div>

          <div class="flex h-full w-full items-center justify-end pt-2 sm:pt-0">
            <div class="flex h-9 items-center justify-center">
              <div
                @click="
                  product.quantity > 1
                    ? handleUpdateQuantity('decrease', product.variant.id, 1)
                    : ''
                "
                class="flex h-full items-center justify-center rounded-l-lg border border-zinc-200 p-2 hover:bg-gray-50"
                :class="{
                  'cursor-not-allowed opacity-50': product.quantity <= 1,
                  'cursor-pointer': product.quantity > 1
                }"
              >
                <Icon name="remove" type="filled"></Icon>
              </div>

              <div
                class="flex h-full items-center justify-center border border-zinc-200 px-4 py-2"
              >
                <transition
                  enter-active-class="transition-all duration-300 ease-out"
                  enter-from-class="translate-y-2 opacity-0"
                  enter-to-class="translate-y-0 opacity-100"
                  leave-active-class="transition-all duration-200 ease-in"
                  leave-from-class="translate-y-0 opacity-100"
                  leave-to-class="-translate-y-2 opacity-0"
                  mode="out-in"
                >
                  <span class="text-zinc-800" :key="product.quantity">
                    {{ product.quantity }}</span
                  >
                </transition>
              </div>

              <div
                @click="handleUpdateQuantity('increase', product.variant.id, 1)"
                class="flex h-full cursor-pointer items-center justify-center rounded-r-lg border border-zinc-200 p-2 hover:bg-gray-50"
              >
                <Icon name="add" type="filled"></Icon>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="data.product_variants.length === 0">
        <div class="flex h-full w-full flex-col items-center justify-center">
          <Icon
            name="shopping_cart"
            size="text-7xl"
            type="filled"
            class="pb-2 text-primary"
          ></Icon>
          <span class="text-2xl font-medium text-zinc-800">{{
            __('Your Cart Awaits!')
          }}</span>
          <span class="mt-4 text-center text-base text-zinc-800">
            {{
              __(
                "It looks like you haven't added any products to your cart yet.\
                  To get started, you can add products directly from shop page"
              )
            }}
          </span>
          <button
            class="btn btn-fill hover-lift mt-8 flex w-fit items-center"
            transition="transition-all"
            @click.stop="
              redirectToPage('redesign.candidate.shop.products.index')
            "
          >
            <span class="text-center">
              {{ __('Add Products') }}
            </span>
          </button>
        </div>
      </template>
    </div>

    <div
      v-if="data.product_variants.length > 0"
      class="flex w-full items-end justify-end gap-x-2 pt-2.5"
    >
      <button
        @click="$emit('cancel')"
        class="btn btn-ghost hover-lift-no-shadow"
      >
        <span class="text-primary">
          {{ __('Cancel') }}
        </span>
      </button>
      <button @click="$emit('next')" class="btn btn-fill hover-lift-no-shadow">
        <span>
          {{ __('Confirm Items') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
  import Icon from '@/components/global/Icon.vue';
  import { __, redirectToPage } from '@/helpers';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    }
  });

  const emit = defineEmits([
    'delete-product',
    'next',
    'cancel',
    'update-quantity'
  ]);

  const handleDeleteProduct = async (product_variant_id) => {
    emit('delete-product', product_variant_id);
  };

  const handleUpdateQuantity = (action, product_variant_id, quantity) => {
    emit('update-quantity', {
      action: action,
      product_variant_id: product_variant_id,
      quantity: quantity
    });
  };
</script>
